import { Atom, atom } from "jotai";
import { MathResult } from "../types";
import {
  equityDollarAtom,
  equityInvestorDollarAtom,
  equityInvestorEquityStepUpAtom,
  equityPromoteSearchersCarryPercentAtom,
  equitySearchPhaseCapitalDollarAtom,
  equitySellerRolloverDollarAtom,
  equityTreatmentAcquisitionInvestorsApplyStepUpAtom,
  equityTreatmentAcquisitionInvestorsAtom,
  equityTreatmentSearchPhaseApplyStepUpAtom,
  equityTreatmentSearchPhaseAtom,
  equityTreatmentSearchersCoInvestApplyStepUpAtom,
  equityTreatmentSearchersCoInvestAtom,
  equityTreatmentSellerEquityRolloverApplyStepUpAtom,
  equityTreatmentSellerEquityRolloverAtom,
  isEquityCalculationMethodUsingProjectCostAtom,
  isExternalEquityAtom,
  isInvestorFundedSearchPhaseAtom,
  isSellerRollingOverEquityAtom,
  workingCapitalBuyerCashAtom,
  workingCapitalEquityContributorAtom,
  workingCapitalFundingSourceAtom,
} from "./general";
import { sourcesTotalCostAtom } from "./sourcesAndUses";

////////////////
// Captial Invested
////////////////
export const searchPhaseInvestorsCapitalInvestedAtom = atom((get) => {
  return get(equitySearchPhaseCapitalDollarAtom);
});

export const acquisitionInvestorsCapitalInvestedAtom = atom((get) => {
  const E30 = Number(get(equityInvestorDollarAtom));
  const L6 = Number(get(workingCapitalBuyerCashAtom) || 0);
  const L7 = get(workingCapitalFundingSourceAtom);
  const L9 = get(workingCapitalEquityContributorAtom);

  return E30 + (L7 === "equity" ? (L9 === "investors" ? L6 : 0) : 0);
});

export const searcherCoInvestCapitalInvestedAtom = atom((get) => {
  //return get(equityDollarAtom);
  const E29 = Number(get(equityDollarAtom));
  const G64 = get(equityTreatmentSearchersCoInvestAtom);
  const L6 = Number(get(workingCapitalBuyerCashAtom) || 0);
  const L7 = get(workingCapitalFundingSourceAtom);
  const L9 = get(workingCapitalEquityContributorAtom);

  return G64 === "preferred"
    ? E29 + (L7 === "equity" ? (L9 === "searcher" ? L6 : 0) : 0)
    : 0;
});

export const sellerRolloverCapitalInvestedAtom = atom((get) => {
  return get(equitySellerRolloverDollarAtom);
});

export const totalCapitalInvestedAtom = atom((get) => {
  const searchPhase = get(searchPhaseInvestorsCapitalInvestedAtom);
  const acquisition = get(acquisitionInvestorsCapitalInvestedAtom);
  const coInvest = get(searcherCoInvestCapitalInvestedAtom);
  const sellerRollover = get(sellerRolloverCapitalInvestedAtom);

  return (
    Number(searchPhase) +
    Number(acquisition) +
    Number(coInvest) +
    Number(sellerRollover)
  );
});

export const searcherCoInvestFinanceCapitalInvestedAtom = atom((get) => {
  const coInvestCapital = get(searcherCoInvestCapitalInvestedAtom);

  if (get(equityTreatmentSearchersCoInvestAtom) === "common") {
    return 0;
  }
  return coInvestCapital;
});

export const sellerRolloverFinanceCapitalInvestedAtom = atom((get) => {
  if (get(equityTreatmentSellerEquityRolloverAtom) === "common") {
    return 0;
  }
  return get(equitySellerRolloverDollarAtom);
});

export const totalFinanceCapitalInvestedAtom = atom((get) => {
  const searchPhase = get(searchPhaseInvestorsCapitalInvestedAtom);
  const acquisition = get(acquisitionInvestorsCapitalInvestedAtom);
  const coInvest = get(searcherCoInvestFinanceCapitalInvestedAtom);
  const sellerRollover = get(sellerRolloverFinanceCapitalInvestedAtom);

  return (
    Number(searchPhase) +
    Number(acquisition) +
    Number(coInvest) +
    Number(sellerRollover)
  );
});

export const preStepUpEquityPercentSearchPhaseInvestorsAtom = atom((get) => {
  const total = get(totalFinanceCapitalInvestedAtom);
  const value = Number(get(searchPhaseInvestorsCapitalInvestedAtom)) || 0;
  return total === 0 ? 0 : value / total;
});

export const preStepUpEquityPercentAcquisitionInvestorsAtom = atom((get) => {
  const total = get(totalFinanceCapitalInvestedAtom);
  const value = Number(get(acquisitionInvestorsCapitalInvestedAtom)) || 0;
  return total === 0 ? 0 : value / total;
});

export const preStepUpEquityPercentSearcherCoInvestAtom = atom((get) => {
  const total = get(totalFinanceCapitalInvestedAtom);
  const value = Number(get(searcherCoInvestFinanceCapitalInvestedAtom)) || 0;
  return total === 0 ? 0 : value / total;
});

export const preStepUpEquityPercentSellerRolloverAtom = atom((get) => {
  const total = get(totalFinanceCapitalInvestedAtom);
  const value = Number(get(sellerRolloverFinanceCapitalInvestedAtom)) || 0;
  return total === 0 ? 0 : value / total;
});

////////////////
// Equity Method - Preferred Equity
////////////////

function calculateEquity(row: {
  amountInvested: number;
  stepUpElection: boolean;
  equityType: "common" | "preferred";
  stepUpApplies: boolean;
  multiplier: number;
}): number {
  if (!row.stepUpApplies) return 0;
  if (row.equityType === "common") return 0;
  return row.stepUpElection
    ? row.amountInvested * row.multiplier
    : row.amountInvested;
}

export const searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom = atom(
  (get) => {
    return calculateEquity({
      amountInvested: Number(get(searchPhaseInvestorsCapitalInvestedAtom)),
      stepUpElection: get(equityTreatmentSearchPhaseApplyStepUpAtom),
      equityType: get(equityTreatmentSearchPhaseAtom),
      stepUpApplies: get(isInvestorFundedSearchPhaseAtom),
      multiplier: Number(get(equityInvestorEquityStepUpAtom)),
    });
  },
);

export const acquisitionInvestorsDollarPreferredEquityEquityMethodAtom = atom(
  (get) => {
    return calculateEquity({
      amountInvested: Number(get(acquisitionInvestorsCapitalInvestedAtom)),
      stepUpElection: get(equityTreatmentAcquisitionInvestorsApplyStepUpAtom),
      equityType: get(equityTreatmentAcquisitionInvestorsAtom),
      stepUpApplies: true,
      multiplier: Number(get(equityInvestorEquityStepUpAtom)),
    });
  },
);

export const searcherCoInvestDollarPreferredEquityEquityMethodAtom = atom(
  (get) => {
    const val = calculateEquity({
      amountInvested: Number(get(searcherCoInvestCapitalInvestedAtom)),
      stepUpElection: get(equityTreatmentSearchersCoInvestApplyStepUpAtom),
      equityType: get(equityTreatmentSearchersCoInvestAtom),
      stepUpApplies: true,
      multiplier: Number(get(equityInvestorEquityStepUpAtom)),
    });

    if (!get(isExternalEquityAtom)) return 0;
    return val;
  },
);

export const sellerRolloverDollarPreferredEquityEquityMethodAtom = atom(
  (get) => {
    return calculateEquity({
      amountInvested: Number(get(sellerRolloverCapitalInvestedAtom)),
      stepUpElection: get(equityTreatmentSellerEquityRolloverApplyStepUpAtom),
      equityType: get(equityTreatmentSellerEquityRolloverAtom),
      stepUpApplies: get(isSellerRollingOverEquityAtom),
      multiplier: Number(get(equityInvestorEquityStepUpAtom)),
    });
  },
);

export const preferredEquityTotalDollarValueEquityMethodAtom = atom((get) => {
  const searchPhase = get(
    searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom,
  );
  const acquisition = get(
    acquisitionInvestorsDollarPreferredEquityEquityMethodAtom,
  );
  const coInvest = get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);
  const sellerRollover = get(
    sellerRolloverDollarPreferredEquityEquityMethodAtom,
  );

  return searchPhase + acquisition + coInvest + sellerRollover;
});

export const searchPhaseInvestorsPercentPreferredEquityEquityMethodAtom = atom(
  (get) => {
    const total = get(preferredEquityTotalDollarValueEquityMethodAtom);
    const value = get(
      searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom,
    );

    return total === 0 ? 0 : value / total;
  },
);

export const acquisitionInvestorsPercentPreferredEquityEquityMethodAtom = atom(
  (get) => {
    const total = get(preferredEquityTotalDollarValueEquityMethodAtom);
    const value = get(
      acquisitionInvestorsDollarPreferredEquityEquityMethodAtom,
    );
    return total === 0 ? 0 : value / total;
  },
);

export const searcherCoInvestPercentPreferredEquityEquityMethodAtom = atom(
  (get) => {
    const total = get(preferredEquityTotalDollarValueEquityMethodAtom);
    const value = get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);

    if (!get(isExternalEquityAtom)) return 0;

    return total === 0 ? 0 : value / total;
  },
);

export const sellerRolloverPercentPreferredEquityEquityMethodAtom = atom(
  (get) => {
    const total = get(preferredEquityTotalDollarValueEquityMethodAtom);
    const value = get(sellerRolloverDollarPreferredEquityEquityMethodAtom);
    return total === 0 ? 0 : value / total;
  },
);

////////////////
// Equity Method - Common Equity
////////////////

export const searchPhaseInvestorsDollarCommonEquityEquityMethodAtom = atom(
  (get) => {
    get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom);

    if (!get(isInvestorFundedSearchPhaseAtom)) return 0;
    return get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom);
  },
);

export const acquisitionInvestorsDollarCommonEquityEquityMethodAtom = atom(
  (get) => {
    return get(acquisitionInvestorsDollarPreferredEquityEquityMethodAtom);
  },
);

export const searcherCoInvestDollarCommonEquityEquityMethodAtom = atom(
  (get) => {
    // get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);
    // if (get(equityTreatmentSearchersCoInvestAtom) === "common")
    //   return get(equityDollarAtom);
    // return get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);

    const G64 = get(equityTreatmentSearchersCoInvestAtom);
    const E29 = Number(get(equityDollarAtom));
    const L6 = Number(get(workingCapitalBuyerCashAtom) || 0);
    const L7 = get(workingCapitalFundingSourceAtom);
    const L9 = get(workingCapitalEquityContributorAtom);
    const D75 = Number(
      get(searcherCoInvestDollarPreferredEquityEquityMethodAtom),
    );

    return G64 === "common"
      ? E29 + (L7 === "equity" ? (L9 === "searcher" ? L6 : 0) : 0)
      : D75;
  },
);

export const sellerRolloverDollarCommonEquityEquityMethodAtom = atom((get) => {
  get(sellerRolloverDollarPreferredEquityEquityMethodAtom);
  get(isSellerRollingOverEquityAtom);
  get(equityTreatmentSellerEquityRolloverAtom);
  get(equitySellerRolloverDollarAtom);

  if (get(isSellerRollingOverEquityAtom) === false) return 0;

  if (get(equityTreatmentSellerEquityRolloverAtom) === "common") {
    return get(equitySellerRolloverDollarAtom);
  }

  return get(sellerRolloverDollarPreferredEquityEquityMethodAtom);
});

export const promoteSearchersCarryDollarCommonEquityEquityMethodAtom = atom(
  (get) => {
    const E29 = Number(get(searcherCoInvestCapitalInvestedAtom)); //Equity (Searcher)
    const E30 = Number(get(equityInvestorDollarAtom)); //Equity (Acquisition Investors)
    const E31 = Number(get(equitySellerRolloverDollarAtom)); //Equity (Seller Rollover)
    const D46 = Number(get(equitySearchPhaseCapitalDollarAtom)); // Equity (Search Phase Investors)

    const prePromote = get(prePromoteCommonEquityDollarEquityMethodAtom);
    const promotePercent = Number(get(equityPromoteSearchersCarryPercentAtom));

    const eq = (prePromote * promotePercent) / (1 - promotePercent);

    return E29 > 0 && E30 === 0 && E31 === 0 && D46 === 0 ? 0 : eq;
  },
);

// (HELPER TABLE [2]): Equity Method → Common Equity ($) ownership:
export const prePromoteCommonEquityDollarEquityMethodAtom = atom((get) => {
  const searchPhase =
    get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom) ||
    get(searchPhaseInvestorsDollarCommonEquityEquityMethodAtom);

  const acquisition =
    get(acquisitionInvestorsDollarPreferredEquityEquityMethodAtom) ||
    get(acquisitionInvestorsDollarCommonEquityEquityMethodAtom);

  const coInvest =
    get(searcherCoInvestDollarPreferredEquityEquityMethodAtom) ||
    get(searcherCoInvestDollarCommonEquityEquityMethodAtom);

  const sellerRollover =
    get(sellerRolloverDollarPreferredEquityEquityMethodAtom) ||
    get(sellerRolloverDollarCommonEquityEquityMethodAtom);

  return (
    Number(searchPhase) +
    Number(acquisition) +
    Number(coInvest) +
    Number(sellerRollover)
  );
});

export const postPromoteCommonEquityDollarEquityMethodAtom = atom((get) => {
  const prePromote = get(prePromoteCommonEquityDollarEquityMethodAtom);
  const promotePercent = Number(get(equityPromoteSearchersCarryPercentAtom));

  return prePromote / (1 - promotePercent);
});

export const commonEquityTotalDollarValueEquityMethodAtom = atom((get) => {
  const searchPhase = get(
    searchPhaseInvestorsDollarCommonEquityEquityMethodAtom,
  );
  const acquisition = get(
    acquisitionInvestorsDollarCommonEquityEquityMethodAtom,
  );
  const coInvest = get(searcherCoInvestDollarCommonEquityEquityMethodAtom);
  const sellerRollover = get(sellerRolloverDollarCommonEquityEquityMethodAtom);
  const promote = get(promoteSearchersCarryDollarCommonEquityEquityMethodAtom);

  return (
    Number(searchPhase) +
    Number(acquisition) +
    Number(coInvest) +
    Number(sellerRollover) +
    Number(promote)
  );
});

export const searchPhaseInvestorsPrePromotePercentCommonEquityEquityMethodAtom =
  atom((get) => {
    const total = get(prePromoteCommonEquityDollarEquityMethodAtom);
    const value = Number(
      get(searchPhaseInvestorsDollarCommonEquityEquityMethodAtom),
    );

    return total === 0 ? 0 : value / total;
  });

export const acquisitionInvestorsPrePromotePercentCommonEquityEquityMethodAtom =
  atom((get) => {
    const total = get(prePromoteCommonEquityDollarEquityMethodAtom);
    const value = Number(
      get(acquisitionInvestorsDollarCommonEquityEquityMethodAtom),
    );
    return total === 0 ? 0 : value / total;
  });

export const searcherCoInvestPrePromotePercentCommonEquityEquityMethodAtom =
  atom((get) => {
    const total = get(prePromoteCommonEquityDollarEquityMethodAtom);
    const value = Number(
      get(searcherCoInvestDollarCommonEquityEquityMethodAtom),
    );
    return total === 0 ? 0 : value / total;
  });

export const sellerRolloverPrePromotePercentCommonEquityEquityMethodAtom = atom(
  (get) => {
    const total = get(prePromoteCommonEquityDollarEquityMethodAtom);
    const value = Number(get(sellerRolloverDollarCommonEquityEquityMethodAtom));
    return total === 0 ? 0 : value / total;
  },
);

function buildPostPromotePercentCommonEquityEquityMethodAtom(
  prePromotePercentAtom: Atom<number>,
) {
  return atom((get) => {
    const percent = Number(get(equityPromoteSearchersCarryPercentAtom));
    return get(prePromotePercentAtom) * (1 - percent);
  });
}

export const searchPhaseInvestorsPercentCommonEquityEquityMethodAtom =
  buildPostPromotePercentCommonEquityEquityMethodAtom(
    searchPhaseInvestorsPrePromotePercentCommonEquityEquityMethodAtom,
  );

export const acquisitionInvestorsPercentCommonEquityEquityMethodAtom =
  buildPostPromotePercentCommonEquityEquityMethodAtom(
    acquisitionInvestorsPrePromotePercentCommonEquityEquityMethodAtom,
  );

const searcherCoInvestPostPromotePercent =
  buildPostPromotePercentCommonEquityEquityMethodAtom(
    searcherCoInvestPrePromotePercentCommonEquityEquityMethodAtom,
  );

export const searcherCoInvestPercentCommonEquityEquityMethodAtom = atom(
  (get) => {
    const E29 = Number(get(searcherCoInvestCapitalInvestedAtom)); //Equity (Searcher)
    const E30 = Number(get(equityInvestorDollarAtom)); //Equity (Acquisition Investors)
    const E31 = Number(get(equitySellerRolloverDollarAtom)); //Equity (Seller Rollover)
    const D46 = Number(get(equitySearchPhaseCapitalDollarAtom)); // Equity (Search Phase Investors)
    const H83 = Number(get(searcherCoInvestPostPromotePercent));

    return E29 === 0 && E30 === 0 && E31 === 0 && D46 === 0
      ? 0
      : E29 > 0 && E30 === 0 && E31 === 0 && D46 === 0
        ? 1
        : H83;
  },
);

export const sellerRolloverPercentCommonEquityEquityMethodAtom =
  buildPostPromotePercentCommonEquityEquityMethodAtom(
    sellerRolloverPrePromotePercentCommonEquityEquityMethodAtom,
  );

export const promoteSearchersCarryPercentCommonEquityEquityMethodAtom = atom(
  (get) => {
    const E29 = Number(get(searcherCoInvestCapitalInvestedAtom)); //Equity (Searcher)
    const E30 = Number(get(equityInvestorDollarAtom)); //Equity (Acquisition Investors)
    const E31 = Number(get(equitySellerRolloverDollarAtom)); //Equity (Seller Rollover)
    const D46 = Number(get(equitySearchPhaseCapitalDollarAtom)); // Equity (Search Phase Investors)
    const H85 = Number(get(equityPromoteSearchersCarryPercentAtom));

    return E29 === 0 && E30 === 0 && E31 === 0 && D46 === 0
      ? 1
      : E29 > 0 && E30 === 0 && E31 === 0 && D46 === 0
        ? 0
        : H85;
  },
);

///////////
// Project Cost Method - Preferred Equity
///////////

export const searchPhaseInvestorsDollarPreferredEquityProjectCostMethodAtom =
  atom((get) => get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom));

export const acquisitionInvestorsDollarPreferredEquityProjectCostMethodAtom =
  atom((get) => get(acquisitionInvestorsDollarPreferredEquityEquityMethodAtom));

export const searcherCoInvestDollarPreferredEquityProjectCostMethodAtom = atom(
  (get) => get(searcherCoInvestDollarPreferredEquityEquityMethodAtom),
);

export const sellerRolloverDollarPreferredEquityProjectCostMethodAtom = atom(
  (get) => get(sellerRolloverDollarPreferredEquityEquityMethodAtom),
);

function calculateIntialPreferredEquity({
  investmentAmount,
  hasStepUp,
  equityType,
  stepUpMultiplier,
  totalEquityDenominator,
}: {
  investmentAmount: number; // D62, D63, etc.
  hasStepUp: boolean; // F62 == "Yes"
  equityType: "common" | "preferred"; // G62
  stepUpMultiplier: number; // $D$52
  totalEquityDenominator: number; // $AB$14
}): number {
  if (equityType === "common") return 0;

  const stepUpAmount =
    (investmentAmount * stepUpMultiplier) / totalEquityDenominator;
  const investmentAmountFraction = investmentAmount / totalEquityDenominator;

  return hasStepUp ? stepUpAmount : investmentAmountFraction;
}

export const searchPhaseInvestorsInitialPercentPreferredEquityProjectCostMethodAtom =
  atom((get) => {
    const investmentAmount = Number(
      get(searchPhaseInvestorsCapitalInvestedAtom),
    );
    const hasStepUp = get(equityTreatmentSearchPhaseApplyStepUpAtom);
    const equityType = get(equityTreatmentSearchPhaseAtom);
    const stepUpMultiplier = Number(get(equityInvestorEquityStepUpAtom));
    const totalEquityDenominator = Number(get(sourcesTotalCostAtom));

    return calculateIntialPreferredEquity({
      investmentAmount,
      hasStepUp,
      equityType,
      stepUpMultiplier,
      totalEquityDenominator,
    });
  });

export const acquisitionInvestorsInitialPercentPreferredEquityProjectCostMethodAtom =
  atom((get) => {
    const investmentAmount = Number(
      get(acquisitionInvestorsCapitalInvestedAtom),
    );
    const hasStepUp = get(equityTreatmentAcquisitionInvestorsApplyStepUpAtom);
    const equityType = get(equityTreatmentAcquisitionInvestorsAtom);
    const stepUpMultiplier = Number(get(equityInvestorEquityStepUpAtom));
    const totalEquityDenominator = Number(get(sourcesTotalCostAtom));

    return calculateIntialPreferredEquity({
      investmentAmount,
      hasStepUp,
      equityType,
      stepUpMultiplier,
      totalEquityDenominator,
    });
  });

export const searcherCoInvestInitialPercentPreferredEquityProjectCostMethodAtom =
  atom((get) => {
    const investmentAmount = Number(get(searcherCoInvestCapitalInvestedAtom));
    const hasStepUp = get(equityTreatmentSearchersCoInvestApplyStepUpAtom);
    const equityType = get(equityTreatmentSearchersCoInvestAtom);
    const stepUpMultiplier = Number(get(equityInvestorEquityStepUpAtom));
    const totalEquityDenominator = Number(get(sourcesTotalCostAtom));

    return calculateIntialPreferredEquity({
      investmentAmount,
      hasStepUp,
      equityType,
      stepUpMultiplier,
      totalEquityDenominator,
    });
  });
export const sellerRolloverInitialPercentPreferredEquityProjectCostMethodAtom =
  atom((get) => {
    const investmentAmount = Number(get(sellerRolloverCapitalInvestedAtom));
    const hasStepUp = get(equityTreatmentSellerEquityRolloverApplyStepUpAtom);
    const equityType = get(equityTreatmentSellerEquityRolloverAtom);
    const stepUpMultiplier = Number(get(equityInvestorEquityStepUpAtom));
    const totalEquityDenominator = Number(get(sourcesTotalCostAtom));

    if (get(isSellerRollingOverEquityAtom) === false) return 0;

    return calculateIntialPreferredEquity({
      investmentAmount,
      hasStepUp,
      equityType,
      stepUpMultiplier,
      totalEquityDenominator,
    });
  });

export const preferredEquityTotalInitialPercentProjectCostMethodAtom = atom(
  (get) => {
    const searchPhase = get(
      searchPhaseInvestorsInitialPercentPreferredEquityProjectCostMethodAtom,
    );
    const acquisition = get(
      acquisitionInvestorsInitialPercentPreferredEquityProjectCostMethodAtom,
    );
    const coInvest = get(
      searcherCoInvestInitialPercentPreferredEquityProjectCostMethodAtom,
    );
    const sellerRollover = get(
      sellerRolloverInitialPercentPreferredEquityProjectCostMethodAtom,
    );

    return (
      Number(searchPhase) +
      Number(acquisition) +
      Number(coInvest) +
      Number(sellerRollover)
    );
  },
);

function buildPreferredEquityPercentProjectCostMethodAtom(
  initialPercentAtom: Atom<number>,
) {
  return atom((get) => {
    const total = get(preferredEquityTotalInitialPercentProjectCostMethodAtom);
    const value = get(initialPercentAtom);

    if (total <= 1) {
      return value;
    }

    return value / total;
  });
}

export const searchPhaseInvestorsPercentPreferredEquityProjectCostMethodAtom =
  buildPreferredEquityPercentProjectCostMethodAtom(
    searchPhaseInvestorsInitialPercentPreferredEquityProjectCostMethodAtom,
  );

export const acquisitionInvestorsPercentPreferredEquityProjectCostMethodAtom =
  buildPreferredEquityPercentProjectCostMethodAtom(
    acquisitionInvestorsInitialPercentPreferredEquityProjectCostMethodAtom,
  );

export const searcherCoInvestPercentPreferredEquityProjectCostMethodAtom =
  buildPreferredEquityPercentProjectCostMethodAtom(
    searcherCoInvestInitialPercentPreferredEquityProjectCostMethodAtom,
  );

export const sellerRolloverPercentPreferredEquityProjectCostMethodAtom =
  buildPreferredEquityPercentProjectCostMethodAtom(
    sellerRolloverInitialPercentPreferredEquityProjectCostMethodAtom,
  );

export const preferredEquityTotalPercentProjectCostMethodAtom = atom((get) => {
  const searchPhase = get(
    searchPhaseInvestorsPercentPreferredEquityProjectCostMethodAtom,
  );
  const acquisition = get(
    acquisitionInvestorsPercentPreferredEquityProjectCostMethodAtom,
  );
  const coInvest = get(
    searcherCoInvestPercentPreferredEquityProjectCostMethodAtom,
  );
  const sellerRollover = get(
    sellerRolloverPercentPreferredEquityProjectCostMethodAtom,
  );

  return (
    Number(searchPhase) +
    Number(acquisition) +
    Number(coInvest) +
    Number(sellerRollover)
  );
});

function buildCommonEquityPercentProjectCostMethodAtom({
  equityTypeAtom,
  preferredAtom,
  investedAtom,
}: {
  equityTypeAtom: Atom<"common" | "preferred">;
  preferredAtom: Atom<number> | Atom<MathResult>;
  investedAtom: Atom<number> | Atom<MathResult>;
}) {
  return atom((get): number => {
    const pValue = get(preferredAtom);

    if (get(equityTypeAtom) === "preferred") {
      return Number(pValue);
    }

    return Number(get(investedAtom)) / Number(get(sourcesTotalCostAtom));
  });
}

export const searchPhaseInvestorsPercentCommonEquityProjectCostMethodAtom =
  buildCommonEquityPercentProjectCostMethodAtom({
    equityTypeAtom: equityTreatmentSearchPhaseAtom,
    preferredAtom:
      searchPhaseInvestorsPercentPreferredEquityProjectCostMethodAtom,
    investedAtom: equitySearchPhaseCapitalDollarAtom,
  });

export const acquisitionInvestorsPercentCommonEquityProjectCostMethodAtom =
  buildCommonEquityPercentProjectCostMethodAtom({
    equityTypeAtom: equityTreatmentAcquisitionInvestorsAtom,
    preferredAtom:
      acquisitionInvestorsPercentPreferredEquityProjectCostMethodAtom,
    investedAtom: equitySellerRolloverDollarAtom,
  });

export const searcherCoInvestPercentCommonEquityProjectCostMethodAtom = atom(
  (get) => {
    const E29 = Number(get(equityDollarAtom)); //Equity (Searcher)
    const E30 = Number(get(equityInvestorDollarAtom)); //Equity (Acquisition Investors)
    const E31 = Number(get(equitySellerRolloverDollarAtom)); //Equity (Seller Rollover)
    const D46 = Number(get(equitySearchPhaseCapitalDollarAtom)); // Equity (Search Phase Investors)
    const G64 = get(equityTreatmentSearchersCoInvestAtom);
    const E96 = Number(
      get(searcherCoInvestPercentPreferredEquityProjectCostMethodAtom),
    );
    const AB14 = Number(get(sourcesTotalCostAtom)); // Total Sources & Uses
    const L7 = get(workingCapitalFundingSourceAtom);
    const L9 = get(workingCapitalEquityContributorAtom);
    const L6 = Number(get(workingCapitalBuyerCashAtom) || 0);

    const searcherEquity =
      E29 + (L7 === "equity" && L9 === "searcher" ? L6 : 0);

    const investorEquity =
      E30 + (L7 === "equity" && L9 === "investors" ? L6 : 0);

    return searcherEquity === 0 &&
      investorEquity === 0 &&
      E31 === 0 &&
      D46 === 0
      ? 0
      : searcherEquity > 0 && investorEquity === 0 && E31 === 0 && D46 === 0
        ? 1
        : G64 === "preferred"
          ? E96
          : searcherEquity / AB14;
  },
);

export const sellerRolloverPercentCommonEquityProjectCostMethodAtom =
  buildCommonEquityPercentProjectCostMethodAtom({
    equityTypeAtom: equityTreatmentSellerEquityRolloverAtom,
    preferredAtom: sellerRolloverPercentPreferredEquityProjectCostMethodAtom,
    investedAtom: equitySellerRolloverDollarAtom,
  });

export const promoteSearchersCarryPercentCommonEquityProjectCostMethodAtom =
  atom((get) => {
    // sum all common equity amounts
    const searchPhase = get(
      searchPhaseInvestorsPercentCommonEquityProjectCostMethodAtom,
    );
    const acquisition = get(
      acquisitionInvestorsPercentCommonEquityProjectCostMethodAtom,
    );
    const coInvest = get(
      searcherCoInvestPercentCommonEquityProjectCostMethodAtom,
    );
    const sellerRollover = get(
      sellerRolloverPercentCommonEquityProjectCostMethodAtom,
    );

    const totalCommonEquity =
      Number(searchPhase) +
      Number(acquisition) +
      Number(coInvest) +
      Number(sellerRollover);

    return 1 - totalCommonEquity;
  });

/////////////
// Project Cost Method - Common Equity
/////////////
// [Invested Capital] * [Step-Up Multiple] ← Search Phase Investors
// [Invested Capital] * [Step-Up Multiple] ← Acquisition Investors
// [Invested Capital] * [Step-Up Multiple] ← Searcher Co-Invest
// [Invested Capital] * [Step-Up Multiple] ← Seller Equity Rollover
// (Keep this as-is; it’s different than the ones above) ← Promote Searcher’s Carry
function calculateCommonEquityProjectCost(
  investedCapitalAtom: Atom<MathResult> | Atom<number>,
  stepUpMultipleAtom: Atom<MathResult> | Atom<number>,
) {
  return atom((get) => {
    const investedCapital = Number(get(investedCapitalAtom));
    const stepUpMultiple = Number(get(stepUpMultipleAtom));
    return investedCapital * stepUpMultiple;
  });
}

const searchPhaseInvestorsDollarCommonEquityProjectCostMethodAtom =
  calculateCommonEquityProjectCost(
    searchPhaseInvestorsCapitalInvestedAtom,
    equityInvestorEquityStepUpAtom,
  );

const acquisitionInvestorsDollarCommonEquityProjectCostMethodAtom =
  calculateCommonEquityProjectCost(
    acquisitionInvestorsCapitalInvestedAtom,
    equityInvestorEquityStepUpAtom,
  );

const searcherCoInvestDollarCommonEquityProjectCostMethodAtom =
  calculateCommonEquityProjectCost(
    searcherCoInvestCapitalInvestedAtom,
    atom((get) => {
      return get(equityTreatmentSearchersCoInvestAtom) === "preferred" &&
        get(equityTreatmentSearchersCoInvestApplyStepUpAtom)
        ? get(equityInvestorEquityStepUpAtom)
        : 1;
    }),
  );

const sellerRolloverDollarCommonEquityProjectCostMethodAtom =
  calculateCommonEquityProjectCost(
    sellerRolloverCapitalInvestedAtom,
    atom((get) => {
      return get(equityTreatmentSellerEquityRolloverAtom) === "preferred" &&
        get(isSellerRollingOverEquityAtom) &&
        get(equityTreatmentSellerEquityRolloverApplyStepUpAtom)
        ? get(equityInvestorEquityStepUpAtom)
        : 1;
    }),
  );

// Results
function buildEquitySwitchingAtom<T>(
  projectCostMethodAtom: Atom<T>,
  equityMethodAtom: Atom<T>,
) {
  return atom((get) => {
    // Touch both for dependency tracking
    get(projectCostMethodAtom);
    get(equityMethodAtom);

    return get(isEquityCalculationMethodUsingProjectCostAtom)
      ? get(projectCostMethodAtom)
      : get(equityMethodAtom);
  });
}

export const searchPhaseInvestorsPercentPreferredEquityAtom =
  buildEquitySwitchingAtom(
    searchPhaseInvestorsPercentPreferredEquityProjectCostMethodAtom,
    searchPhaseInvestorsPercentPreferredEquityEquityMethodAtom,
  );

export const acquisitionInvestorsPercentPreferredEquityAtom =
  buildEquitySwitchingAtom(
    acquisitionInvestorsPercentPreferredEquityProjectCostMethodAtom,
    acquisitionInvestorsPercentPreferredEquityEquityMethodAtom,
  );

export const coInvestorsPercentPreferredEquityAtom = buildEquitySwitchingAtom(
  searcherCoInvestPercentPreferredEquityProjectCostMethodAtom,
  searcherCoInvestPercentPreferredEquityEquityMethodAtom,
);

export const sellerRolloverPercentPreferredEquityAtom =
  buildEquitySwitchingAtom(
    sellerRolloverPercentPreferredEquityProjectCostMethodAtom,
    sellerRolloverPercentPreferredEquityEquityMethodAtom,
  );

export const searchPhaseInvestorsPercentCommonEquityAtom =
  buildEquitySwitchingAtom(
    searchPhaseInvestorsPercentCommonEquityProjectCostMethodAtom,
    searchPhaseInvestorsPercentCommonEquityEquityMethodAtom,
  );

export const acquisitionInvestorsPercentCommonEquityAtom =
  buildEquitySwitchingAtom(
    acquisitionInvestorsPercentCommonEquityProjectCostMethodAtom,
    acquisitionInvestorsPercentCommonEquityEquityMethodAtom,
  );

export const searcherCoInvestPercentCommonEquityAtom = buildEquitySwitchingAtom(
  searcherCoInvestPercentCommonEquityProjectCostMethodAtom,
  searcherCoInvestPercentCommonEquityEquityMethodAtom,
);

export const sellerRolloverPercentCommonEquityAtom = buildEquitySwitchingAtom(
  sellerRolloverPercentCommonEquityProjectCostMethodAtom,
  sellerRolloverPercentCommonEquityEquityMethodAtom,
);

export const promoteSearchersCarryPercentCommonEquityAtom =
  buildEquitySwitchingAtom(
    promoteSearchersCarryPercentCommonEquityProjectCostMethodAtom,
    promoteSearchersCarryPercentCommonEquityEquityMethodAtom,
  );

export const preferredEquityTotalPercentAtom = atom((get) => {
  return (
    get(searchPhaseInvestorsPercentPreferredEquityAtom) +
    get(acquisitionInvestorsPercentPreferredEquityAtom) +
    get(coInvestorsPercentPreferredEquityAtom) +
    get(sellerRolloverPercentPreferredEquityAtom)
  );
});

export const commonEquityTotalPercentAtom = atom((get) => {
  return (
    Number(get(searchPhaseInvestorsPercentCommonEquityAtom)) +
    Number(get(acquisitionInvestorsPercentCommonEquityAtom)) +
    Number(get(searcherCoInvestPercentCommonEquityAtom)) +
    Number(get(sellerRolloverPercentCommonEquityAtom)) +
    Number(get(promoteSearchersCarryPercentCommonEquityAtom))
  );
});

export const searchPhaseInvestorsDollarPreferredEquityAtom = atom((get) => {
  get(searchPhaseInvestorsDollarPreferredEquityProjectCostMethodAtom);
  get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom);

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(searchPhaseInvestorsDollarPreferredEquityProjectCostMethodAtom)
    : get(searchPhaseInvestorsDollarPreferredEquityEquityMethodAtom);
});

export const acquisitionInvestorsDollarPreferredEquityAtom = atom((get) => {
  get(acquisitionInvestorsDollarPreferredEquityProjectCostMethodAtom);
  get(acquisitionInvestorsDollarPreferredEquityEquityMethodAtom);

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(acquisitionInvestorsDollarPreferredEquityProjectCostMethodAtom)
    : get(acquisitionInvestorsDollarPreferredEquityEquityMethodAtom);
});

export const searcherCoInvestDollarPreferredEquityAtom = atom((get) => {
  get(searcherCoInvestDollarPreferredEquityProjectCostMethodAtom);
  get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(searcherCoInvestDollarPreferredEquityProjectCostMethodAtom)
    : get(searcherCoInvestDollarPreferredEquityEquityMethodAtom);
});

export const sellerRolloverDollarPreferredEquityAtom = atom((get) => {
  get(sellerRolloverDollarPreferredEquityProjectCostMethodAtom);
  get(sellerRolloverDollarPreferredEquityEquityMethodAtom);

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(sellerRolloverDollarPreferredEquityProjectCostMethodAtom)
    : get(sellerRolloverDollarPreferredEquityEquityMethodAtom);
});

export const totalPreferredEquityDollarValueAtom = atom((get) => {
  return (
    get(searchPhaseInvestorsDollarPreferredEquityAtom) +
    get(acquisitionInvestorsDollarPreferredEquityAtom) +
    get(searcherCoInvestDollarPreferredEquityAtom) +
    get(sellerRolloverDollarPreferredEquityAtom)
  );
});

export const searchPhaseInvestorsDollarCommonEquityAtom = atom((get) => {
  get(searchPhaseInvestorsDollarCommonEquityEquityMethodAtom);

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(searchPhaseInvestorsDollarCommonEquityProjectCostMethodAtom)
    : get(searchPhaseInvestorsDollarCommonEquityEquityMethodAtom);
});

export const acquisitionInvestorsDollarCommonEquityAtom = atom((get) => {
  get(acquisitionInvestorsDollarCommonEquityEquityMethodAtom);
  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(acquisitionInvestorsDollarCommonEquityProjectCostMethodAtom)
    : get(acquisitionInvestorsDollarCommonEquityEquityMethodAtom);
});

export const searcherCoInvestDollarCommonEquityAtom = atom((get) => {
  get(searcherCoInvestDollarCommonEquityEquityMethodAtom);
  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(searcherCoInvestDollarCommonEquityProjectCostMethodAtom)
    : get(searcherCoInvestDollarCommonEquityEquityMethodAtom);
});

export const sellerRolloverDollarCommonEquityAtom = atom((get) => {
  get(sellerRolloverDollarCommonEquityEquityMethodAtom);
  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? get(sellerRolloverDollarCommonEquityProjectCostMethodAtom)
    : get(sellerRolloverDollarCommonEquityEquityMethodAtom);
});

export const promoteSearchersCarryDollarCommonEquityAtom = atom((get) => {
  get(promoteSearchersCarryPercentCommonEquityProjectCostMethodAtom);
  get(promoteSearchersCarryDollarCommonEquityEquityMethodAtom);

  if (!get(isInvestorFundedSearchPhaseAtom)) return 0;

  return get(isEquityCalculationMethodUsingProjectCostAtom)
    ? Number(
        get(promoteSearchersCarryPercentCommonEquityProjectCostMethodAtom),
      ) * get(sourcesTotalCostAtom)
    : get(promoteSearchersCarryDollarCommonEquityEquityMethodAtom);
});

export const totalCommonEquityDollarValueAtom = atom((get) => {
  return (
    Number(get(searchPhaseInvestorsDollarCommonEquityAtom) || 0) +
    Number(get(acquisitionInvestorsDollarCommonEquityAtom) || 0) +
    Number(get(searcherCoInvestDollarCommonEquityAtom) || 0) +
    Number(get(sellerRolloverDollarCommonEquityAtom) || 0) +
    Number(get(promoteSearchersCarryDollarCommonEquityAtom) || 0)
  );
});
